/**
 * DoorComponent - manages door state and animation logic.
 * Handles hinged, sliding, garage, and double door types with proximity-based interaction.
 */

import * as TWEEN from '@tweenjs/tween.js';
import * as THREE from 'three';

import { Actor } from '../../actors/Actor.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { Delegate } from '../../utils/Delegate.js';
import { property } from '../../utils/serialization/decorator.js';
import { ModelMeshComponent } from '../visual/ModelMeshComponent.js';

import { ProximityInteractableComponent, type ProximityInteractableOptions } from './ProximityInteractableComponent.js';

export enum DoorState {
  Closed = 'closed',
  Opening = 'opening',
  Open = 'open',
  Closing = 'closing',
  Locked = 'locked'
}

/**
 * Door animation types
 */
export enum DoorType {
  /** Rotates around Y-axis (fully implemented) */
  Hinged = 'hinged',
  /** Slides along X-axis (fully implemented) */
  Sliding = 'sliding',
  /** Rotates and lifts upward (basic implementation) */
  Garage = 'garage',
  /** Multiple coordinated door panels (placeholder - uses hinged animation) */
  Double = 'double',
  /** Custom animation handled by external code via delegates */
  Custom = 'custom'
}

/**
 * Door interaction methods
 */
export enum DoorInteractionType {
  /** Automatically opens when player approaches, closes when they leave */
  Proximity = 'proximity',
  /** Requires player to be in proximity and press interaction key */
  KeyPress = 'keypress',
  /** Reserved for future remote control implementation */
  Remote = 'remote'
}

export interface DoorComponentOptions extends ProximityInteractableOptions {
  doorType?: DoorType;
  interactionType?: DoorInteractionType;
  animationDuration?: number;
  maxOpenAngle?: number;
  slideDistance?: number;
  /** Direction vector for sliding doors in local space (default: right direction) */
  slideDirection?: THREE.Vector3;
  startLocked?: boolean;
}

export type DoorEventDelegate = Delegate<[DoorComponent], void>;

@EngineClass('DoorComponent')
export class DoorComponent extends ProximityInteractableComponent {
  @property()
  protected doorType: DoorType = DoorType.Hinged;

  @property()
  protected interactionType: DoorInteractionType = DoorInteractionType.Proximity;

  @property({ type: 'number', min: 0.1, step: 0.1, decimals: 1, description: 'Animation duration in seconds' })
  protected animationDuration: number = 1.0;

  @property({ type: 'number', min: 0, max: 180, step: 1, decimals: 0, description: 'Maximum open angle for hinged doors (degrees)' })
  protected maxOpenAngle: number = 90;

  @property({ type: 'number', min: 0, step: 0.1, decimals: 1, description: 'Slide distance for sliding doors' })
  protected slideDistance: number = 2.0;

  @property({ description: 'Direction vector for sliding doors in local space' })
  protected slideDirection: THREE.Vector3 = new THREE.Vector3(1, 0, 0);

  @property({ description: 'Whether the door starts in locked state' })
  protected startLocked: boolean = false;

  // State management
  private currentState: DoorState = DoorState.Closed;
  private animationProgress: number = 0;

  // Tween.js animation
  private tweenGroup: TWEEN.Group = new TWEEN.Group();
  private currentTween: TWEEN.Tween<{ progress: number }> | null = null;

  // Transform tracking
  private initialTransform: { position: THREE.Vector3; rotation: THREE.Euler; scale: THREE.Vector3 } | null = null;
  private doorMesh: THREE.Mesh | null = null;
  private modelMeshComponent: ModelMeshComponent | null = null;

  // Event delegates
  public readonly onDoorStartOpening: DoorEventDelegate = new Delegate<[DoorComponent], void>();
  public readonly onDoorStartClosing: DoorEventDelegate = new Delegate<[DoorComponent], void>();
  public readonly onDoorOpened: DoorEventDelegate = new Delegate<[DoorComponent], void>();
  public readonly onDoorClosed: DoorEventDelegate = new Delegate<[DoorComponent], void>();
  public readonly onDoorLocked: DoorEventDelegate = new Delegate<[DoorComponent], void>();
  public readonly onDoorUnlocked: DoorEventDelegate = new Delegate<[DoorComponent], void>();
  public readonly onInteractionAttempt: DoorEventDelegate = new Delegate<[DoorComponent], void>();

  public override initialize(options?: DoorComponentOptions): void {
    super.initialize(options);
    this.doorType = options?.doorType ?? this.doorType;
    this.interactionType = options?.interactionType ?? this.interactionType;
    this.animationDuration = options?.animationDuration ?? this.animationDuration;
    this.maxOpenAngle = options?.maxOpenAngle ?? this.maxOpenAngle;
    this.slideDistance = options?.slideDistance ?? this.slideDistance;
    this.slideDirection = options?.slideDirection ?? this.slideDirection;
    this.startLocked = options?.startLocked ?? this.startLocked;

    // Configure base class interaction mode
    this.useKeyPressInteraction = this.interactionType === DoorInteractionType.KeyPress;

    if (this.startLocked) {
      this.currentState = DoorState.Locked;
    }
  }

  public override postLoad(): void {
    super.postLoad();
    if (this.startLocked) {
      this.currentState = DoorState.Locked;
    }
  }

  public override beginPlay(): void {
    // Initialize door geometry before setting up proximity detection
    this.initializeDoorGeometry();

    // Call parent beginPlay (sets up proximity detection)
    super.beginPlay();
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    // Update tween animations
    this.tweenGroup.update(this.getGameTimeMS());
  }

  /**
   * Opens the door if it's not locked.
   * Can interrupt a closing animation to smoothly reverse direction.
   */
  public open(): void {
    if (this.currentState === DoorState.Locked) {
      this.onDoorLocked.invoke(this);
      return;
    }

    // Allow opening from Closed or Closing states (can interrupt closing animation)
    if (this.currentState === DoorState.Closed || this.currentState === DoorState.Closing) {
      this.startOpening();
    }
  }

  /**
   * Closes the door.
   * Can interrupt an opening animation to smoothly reverse direction.
   */
  public close(): void {
    // Allow closing from Open or Opening states (can interrupt opening animation)
    if (this.currentState === DoorState.Open || this.currentState === DoorState.Opening) {
      this.startClosing();
    }
  }

  private getGameTimeMS(): number {
    return (this.getWorld()?.getGameTime() ?? 0) * 1000;
  }

  /**
   * Starts opening animation, can interrupt closing animation smoothly
   */
  private startOpening(): void {
    if (this.currentState === DoorState.Locked) {
      this.onDoorLocked.invoke(this);
      return;
    }

    this.currentState = DoorState.Opening;
    this.onDoorStartOpening.invoke(this);

    // Stop any existing tween
    if (this.currentTween) {
      this.currentTween.stop();
      this.currentTween = null;
    }

    // Create tween from current progress to fully open (1.0)
    const startProgress = this.animationProgress;
    const targetProgress = 1.0;
    const progressDelta = targetProgress - startProgress;
    const duration = this.animationDuration * progressDelta * 1000;

    const tweenData = { progress: startProgress };

    this.currentTween = new TWEEN.Tween(tweenData, this.tweenGroup)
      .to({ progress: targetProgress }, duration)
      .easing(TWEEN.Easing.Cubic.InOut)
      .onUpdate(() => {
        this.animationProgress = tweenData.progress;
        this.applyDoorTransform(this.animationProgress);
      })
      .onComplete(() => {
        this.currentState = DoorState.Open;
        this.currentTween = null;
        this.onDoorOpened.invoke(this);
      })
      .start(this.getGameTimeMS());
  }

  /**
   * Starts closing animation, can interrupt opening animation smoothly
   */
  private startClosing(): void {
    this.currentState = DoorState.Closing;
    this.onDoorStartClosing.invoke(this);

    // Stop any existing tween
    if (this.currentTween) {
      this.currentTween.stop();
      this.currentTween = null;
    }

    // Create tween from current progress to fully closed (0.0)
    const startProgress = this.animationProgress;
    const targetProgress = 0.0;
    const progressDelta = Math.abs(targetProgress - startProgress);
    const duration = this.animationDuration * progressDelta * 1000;

    const tweenData = { progress: startProgress };

    this.currentTween = new TWEEN.Tween(tweenData, this.tweenGroup)
      .to({ progress: targetProgress }, duration)
      .easing(TWEEN.Easing.Cubic.InOut)
      .onUpdate(() => {
        this.animationProgress = tweenData.progress;
        this.applyDoorTransform(this.animationProgress);
      })
      .onComplete(() => {
        this.currentState = DoorState.Closed;
        this.currentTween = null;
        this.onDoorClosed.invoke(this);
      })
      .start(this.getGameTimeMS());
  }

  /**
   * Toggles the door state
   */
  public toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Locks the door
   */
  public lock(): void {
    this.currentState = DoorState.Locked;
    this.onDoorLocked.invoke(this);
  }

  /**
   * Unlocks the door
   */
  public unlock(): void {
    if (this.currentState === DoorState.Locked) {
      this.currentState = DoorState.Closed;
      this.onDoorUnlocked.invoke(this);
    }
  }

  /**
   * Checks if the door is currently open
   */
  public isOpen(): boolean {
    return this.currentState === DoorState.Open;
  }

  /**
   * Checks if the door is currently locked
   */
  public isLocked(): boolean {
    return this.currentState === DoorState.Locked;
  }

  /**
   * Checks if the door is currently animating
   */
  public isAnimating(): boolean {
    return this.currentState === DoorState.Opening || this.currentState === DoorState.Closing;
  }

  /**
   * Gets the current door state
   */
  public getState(): DoorState {
    return this.currentState;
  }

  // IInteractable implementation
  public override beginInteract(player: Actor): boolean {
    if (!this.canInteract(player)) return false;
    this.onInteractionAttempt.invoke(this);
    this.toggle();
    return true;
  }

  public override canInteract(player: Actor): boolean {
    // Only manual mode can be interacted with via key press
    // Proximity mode is automatic
    return (
      this.isPlayerInRange(player) &&
      !this.isLocked() &&
      this.interactionType === DoorInteractionType.KeyPress
    );
  }

  public override getInteractionPrompt(): string {
    return this.isLocked() ? 'Locked' : 'Open Door';
  }

  public override getInteractionPriority(): number {
    return 100; // High priority for doors
  }

  /**
   * Override to calculate trigger position based on door geometry
   */
  protected override calculateTriggerPosition(): THREE.Vector3 {
    const actor = this.getActor();
    if (!actor) return new THREE.Vector3();

    const doorPosition = actor.rootComponent.position.clone();
    let triggerPosition = doorPosition.clone();

    if (this.doorMesh) {
      // If we have door mesh info, position the trigger at the center of the door panel
      this.doorMesh.geometry.computeBoundingBox();
      const boundingBox = this.doorMesh.geometry.boundingBox;
      if (boundingBox) {
        const doorCenter = new THREE.Vector3();
        boundingBox.getCenter(doorCenter);
        const worldCenter = doorCenter.clone();
        this.doorMesh.localToWorld(worldCenter);
        triggerPosition.copy(worldCenter);
        triggerPosition.y = doorPosition.y + 1.25;
      }
    } else {
      // Fallback: offset the trigger towards the door panel
      const doorForward = new THREE.Vector3(0, 0, 1);
      const offsetDistance = 0.4;
      triggerPosition.add(doorForward.multiplyScalar(offsetDistance));
      triggerPosition.y += 1.25;
    }

    return triggerPosition;
  }

  /**
   * Override to handle automatic proximity-based open/close
   */
  protected override onFirstPlayerEntered(): void {
    // Only auto-open in Proximity mode
    if (this.interactionType === DoorInteractionType.Proximity) {
      if (this.currentState === DoorState.Closed || this.currentState === DoorState.Closing) {
        this.startOpening();
      }
    }
  }

  /**
   * Override to handle automatic proximity-based close when last player leaves
   */
  protected override onLastPlayerLeft(): void {
    // Only auto-close in Proximity mode
    if (this.interactionType === DoorInteractionType.Proximity) {
      if (this.currentState === DoorState.Open || this.currentState === DoorState.Opening) {
        this.startClosing();
      }
    }
  }

  /**
   * Initializes door geometry and calculates pivot point
   */
  private initializeDoorGeometry(): void {
    const actor = this.getActor();
    if (!actor) return;

    // Get the GLTF mesh component from the root scene component hierarchy
    const gltfComponent = actor.rootComponent.getComponent(ModelMeshComponent);
    if (!gltfComponent) {
      console.warn('[DoorComponent] No GLTF mesh component found');
      return;
    }

    this.modelMeshComponent = gltfComponent;

    const model = gltfComponent.getModel();
    if (!model) {
      // if the model is not loaded, wait for it to load and then call this function again
      this.modelMeshComponent.onMeshLoaded.add(() => {
        this.initializeDoorGeometry();
      });
      return;
    }

    // Store initial transform of the GLTF mesh component (what we'll be animating)
    this.initialTransform = {
      position: gltfComponent.position.clone(),
      rotation: gltfComponent.rotation.clone(),
      scale: gltfComponent.scale.clone()
    };

    // Find the door mesh and calculate pivot point
    this.findDoorMesh(model);
    this.calculatePivotPoint();
  }

  /**
   * Finds the main door mesh in the model
   */
  private findDoorMesh(model: THREE.Object3D): void {
    let bestCandidate: THREE.Mesh | null = null;
    let bestScore = 0;

    model.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        let score = 0;

        const name = object.name.toLowerCase();
        if (name.includes('door')) score += 10;
        if (name.includes('panel')) score += 5;
        if (name.includes('leaf')) score += 5;

        // Check dimensions (doors are typically tall and rectangular)
        const bbox = new THREE.Box3().setFromObject(object);
        const size = bbox.getSize(new THREE.Vector3());

        if (size.y > 1.5 && size.y > size.x && size.y > size.z) {
          score += 5;
        }

        if (score > bestScore) {
          bestScore = score;
          bestCandidate = object;
        }
      }
    });

    this.doorMesh = bestCandidate;
  }

  /**
   * Calculates the appropriate pivot point for door animation
   */
  private calculatePivotPoint(): void {
    if (!this.doorMesh) return;

    const bbox = new THREE.Box3().setFromObject(this.doorMesh);

    switch (this.doorType) {
      case DoorType.Hinged:
        bbox.getCenter(new THREE.Vector3());
        break;

      case DoorType.Sliding:
        break;

      case DoorType.Garage:
        bbox.getCenter(new THREE.Vector3());
        break;

      case DoorType.Double:
        bbox.getCenter(new THREE.Vector3());
        break;
    }
  }

  /**
   * Applies transform to door based on animation progress
   */
  private applyDoorTransform(progress: number): void {
    if (!this.initialTransform || !this.modelMeshComponent) return;

    const actor = this.getActor();
    if (!actor) return;

    switch (this.doorType) {
      case DoorType.Hinged:
        this.applyHingedTransform(this.modelMeshComponent, progress);
        break;

      case DoorType.Sliding:
        this.applySlidingTransform(this.modelMeshComponent, actor, progress);
        break;

      case DoorType.Garage:
        this.applyGarageTransform(this.modelMeshComponent, progress);
        break;

      case DoorType.Double:
        this.applyDoubleTransform(this.modelMeshComponent, progress);
        break;

      case DoorType.Custom:
        // Custom doors handle their own animation via delegates
        break;
    }
  }

  /**
   * Applies hinged door animation
   */
  private applyHingedTransform(gltfComponent: ModelMeshComponent, progress: number): void {
    if (!this.initialTransform) return;

    const openAngle = THREE.MathUtils.degToRad(this.maxOpenAngle) * progress;

    gltfComponent.position.copy(this.initialTransform.position);
    gltfComponent.rotation.copy(this.initialTransform.rotation);

    const actor = this.getActor();
    if (actor) {
      const actorWorldRotation = actor.getWorldRotation();
      const localHingeAxis = new THREE.Vector3(0, 1, 0);
      const worldHingeAxis = localHingeAxis.clone().applyEuler(actorWorldRotation);

      const hingeQuaternion = new THREE.Quaternion();
      hingeQuaternion.setFromAxisAngle(worldHingeAxis, openAngle);

      const currentQuaternion = new THREE.Quaternion().setFromEuler(gltfComponent.rotation);
      currentQuaternion.multiply(hingeQuaternion);

      gltfComponent.rotation.setFromQuaternion(currentQuaternion);
    } else {
      gltfComponent.rotation.y += openAngle;
    }
  }

  /**
   * Applies sliding door animation
   */
  private applySlidingTransform(gltfComponent: ModelMeshComponent, actor: Actor, progress: number): void {
    if (!this.initialTransform) return;

    const slideOffset = this.slideDistance * progress;

    gltfComponent.position.copy(this.initialTransform.position);
    gltfComponent.rotation.copy(this.initialTransform.rotation);

    const worldSlideVector = this.slideDirection.clone().normalize();
    const actorWorldRotation = actor.getWorldRotation();
    worldSlideVector.applyEuler(actorWorldRotation);

    const slideVector = worldSlideVector.multiplyScalar(slideOffset);
    gltfComponent.position.add(slideVector);
  }

  /**
   * Applies garage door animation (combination of rotation and translation)
   */
  private applyGarageTransform(gltfComponent: ModelMeshComponent, progress: number): void {
    if (!this.initialTransform) return;

    const maxAngle = THREE.MathUtils.degToRad(90) * progress;
    const liftHeight = 2.0 * progress;

    gltfComponent.position.copy(this.initialTransform.position);
    gltfComponent.rotation.copy(this.initialTransform.rotation);

    gltfComponent.rotation.x -= maxAngle;
    gltfComponent.position.y += liftHeight;
  }

  /**
   * Applies double door animation
   */
  private applyDoubleTransform(gltfComponent: ModelMeshComponent, progress: number): void {
    this.applyHingedTransform(gltfComponent, progress);
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Door';
  }
}
